/**
 * lieferModell — EINE reine Funktion, die aus dem Urteil des Kanal-Wächters
 * entscheidet, ob und wie an einer Kanalkarte ein Lieferhinweis erscheint.
 *
 * WOFÜR (Fund 22.08.2026)
 * ----------------------
 * Die Kanalkarte beantwortet bisher genau eine Frage: „ist der Zugang verbunden?"
 * Der Kunde hat aber eine andere: „kommt bei meinen Lesern wirklich an, was ich
 * plane?" Zwischen beidem liegt der Zustand, der vom 16. bis 20.08. unbemerkt blieb:
 * Verbindung tadellos, Beiträge online — und **jedes Bild fehlte**. 28 von 58
 * Beiträgen auf dem Inhaber-Konto, dazu welche beim zahlenden Kunden.
 *
 * Der Wächter (`social_connect/kanal_wache.py`) misst das seit Runde 7 jeden Morgen
 * und schreibt sein Urteil nach `kanal_zustand`. Gelesen hat es niemand — dieselbe
 * Bauart wie das Fachfreigabe-Tor: gebaut, aber nicht sichtbar.
 *
 * BEWUSST ZURÜCKHALTEND
 * ---------------------
 * `gesund` bekommt KEINEN Hinweis. Eine Karte, die bei jedem Kanal etwas anzeigt,
 * trainiert das Auge, alles zu überlesen — dann übersieht man auch die eine Zeile,
 * auf die es ankommt. Angezeigt wird nur, was eine Handlung oder eine Einordnung
 * verlangt.
 *
 * Kein React, keine Seiteneffekte (gleiche Linie wie `channelButtonModel.js`).
 */

/** Wie dringlich ist ein Urteil? Bestimmt Ton und Reihenfolge. */
export const TON = {
  STOERUNG: "stoerung",   // rot — hier kommt nichts an
  HINWEIS: "hinweis",     // gelb — kommt an, aber nicht vollständig
  RUHIG: "ruhig",         // grau — Einordnung, keine Handlung
};

const URTEILE = {
  gestoert: {
    ton: TON.STOERUNG,
    titel: "Hier kommt gerade nichts an",
  },
  teilweise: {
    ton: TON.HINWEIS,
    titel: "Kommt an — aber nicht vollständig",
  },
  eingeschraenkt: {
    ton: TON.HINWEIS,
    titel: "Vorübergehend eingeschränkt",
  },
  pausiert: {
    ton: TON.RUHIG,
    titel: "Bewusst stillgelegt",
  },
  unbekannt: {
    ton: TON.RUHIG,
    titel: "Noch keine Erfahrung mit diesem Kanal",
  },
};

/**
 * Entscheidet den Lieferhinweis EINES Kanals.
 *
 * @param {object} plattform — Eintrag aus `platforms` der Status-Antwort.
 * @returns {{zeigen: boolean, ton?: string, titel?: string, grund?: string,
 *            geprueft_am?: string}}
 */
export function lieferModell(plattform) {
  const nichts = { zeigen: false };
  if (!plattform || typeof plattform !== "object") return nichts;

  const lieferung = plattform.lieferung;
  if (!lieferung || typeof lieferung !== "object") return nichts;

  // `gesund` und alles Unbekannte aus einer neueren Backend-Fassung: still bleiben.
  // Lieber kein Hinweis als ein Hinweis, den diese Oberfläche nicht deuten kann.
  const eintrag = URTEILE[lieferung.urteil];
  if (!eintrag) return nichts;

  return {
    zeigen: true,
    ton: eintrag.ton,
    titel: eintrag.titel,
    grund: String(lieferung.grund ?? "").trim(),
    geprueft_am: lieferung.geprueft_am ?? null,
  };
}

/** Wie viele Kanäle verlangen eine Handlung? Für eine Sammelzeile über der Liste. */
export function zaehleHandlungsbedarf(platforms) {
  if (!platforms || typeof platforms !== "object") return 0;
  return Object.values(platforms).reduce((n, p) => {
    const m = lieferModell(p);
    return n + (m.zeigen && m.ton !== TON.RUHIG ? 1 : 0);
  }, 0);
}
